import { auth } from '../auth.js';
import { router } from '../router.js';
import { showToast, takeReturnTo, enhanceCodeInput } from '../utils.js';

/**
 * Login, plus both Cognito challenge continuations. v1 had a bespoke `mfa_pending` JWT
 * and a separate email flow for first-time passwords; Cognito expresses both as a
 * challenge + session against the same endpoint, so they're one state machine here.
 */

const app = () => document.getElementById('app');

function setError(message) {
    const el = document.getElementById('form-error');
    if (!el) return;
    el.textContent = message ?? '';
    el.classList.toggle('hidden', !message);
}

function busy(button, isBusy, label) {
    if (!button) return;
    button.disabled = isBusy;
    button.innerHTML = isBusy
        ? '<i class="fa-solid fa-circle-notch fa-spin"></i> Please wait...'
        : label;
}

function shell(title, subtitle, body) {
    return `
        <div class="login-container">
            <h1 class="login-title">${title}</h1>
            <p class="login-subtitle">${subtitle}</p>
            <div id="form-error" class="form-error hidden"></div>
            ${body}
        </div>`;
}

/** Landing state: email + password. */
export function renderLogin() {
    app().innerHTML = shell(
        'Shanks Family',
        'Sign in to continue',
        `
        <form id="login-form" novalidate>
            <div class="form-group">
                <label for="email">Email</label>
                <input type="email" id="email" name="email" autocomplete="username" required autofocus>
            </div>
            <div class="form-group">
                <label for="password">Password</label>
                <input type="password" id="password" name="password" autocomplete="current-password" required>
            </div>
            <button type="submit" id="submit-btn" class="btn btn-primary btn-full-width">Sign in</button>
            <a href="/forgot-password" data-link class="forgot-password-link">Forgotten your password?</a>
        </form>`,
    );

    document.getElementById('login-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        setError(null);

        const email = document.getElementById('email').value.trim();
        const password = document.getElementById('password').value;
        const btn = document.getElementById('submit-btn');

        busy(btn, true);
        try {
            const result = await auth.login(email, password);
            if (result.status === 'challenge') {
                renderChallenge(result);
                return;
            }
            showToast(`Welcome back, ${auth.getUser()?.full_name ?? ''}`.trim(), 'success');
            await router.navigate(takeReturnTo() ?? '/');
        } catch (err) {
            setError(err.message ?? 'Sign in failed');
            busy(btn, false, 'Sign in');
        }
    });
}

/** Dispatches to the right continuation form for whichever challenge Cognito returned. */
function renderChallenge(challenge) {
    if (challenge.challengeName === 'NEW_PASSWORD_REQUIRED') return renderNewPassword(challenge);
    if (challenge.challengeName === 'SOFTWARE_TOKEN_MFA') return renderMfa(challenge);

    // Anything else is a flow this app doesn't implement; say so rather than hanging.
    setError(`This account needs a sign-in step this app can't complete (${challenge.challengeName}). Contact an admin.`);
}

function renderNewPassword(challenge) {
    app().innerHTML = shell(
        'Choose a password',
        'Your account needs a new password before you can sign in.',
        `
        <form id="new-password-form" novalidate>
            <div class="form-group">
                <label for="new-password">New password</label>
                <input type="password" id="new-password" autocomplete="new-password" required autofocus>
                <p class="form-hint">At least 12 characters, with upper and lower case, a number and a symbol.</p>
            </div>
            <div class="form-group">
                <label for="confirm-password">Confirm password</label>
                <input type="password" id="confirm-password" autocomplete="new-password" required>
            </div>
            <button type="submit" id="submit-btn" class="btn btn-primary btn-full-width">Set password and sign in</button>
        </form>`,
    );

    document.getElementById('new-password-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        setError(null);

        const newPassword = document.getElementById('new-password').value;
        const confirm = document.getElementById('confirm-password').value;
        if (newPassword !== confirm) {
            setError('Those passwords do not match.');
            return;
        }

        const btn = document.getElementById('submit-btn');
        busy(btn, true);
        try {
            const result = await auth.completeChallenge({
                email: challenge.email,
                session: challenge.session,
                challengeName: 'NEW_PASSWORD_REQUIRED',
                newPassword,
            });
            if (result.status === 'challenge') {
                renderChallenge(result);
                return;
            }
            showToast('Password set', 'success');
            await router.navigate(takeReturnTo() ?? '/');
        } catch (err) {
            setError(err.message ?? 'Could not set that password');
            busy(btn, false, 'Set password and sign in');
        }
    });
}

function renderMfa(challenge) {
    app().innerHTML = shell(
        'Two-factor code',
        'Enter the 6-digit code from your authenticator app.',
        `
        <form id="mfa-form" novalidate>
            <div class="form-group">
                <label for="code">Authentication code</label>
                <input type="text" id="code" inputmode="numeric" autocomplete="one-time-code"
                       pattern="[0-9]*" maxlength="6" required autofocus>
            </div>
            <button type="submit" id="submit-btn" class="btn btn-primary btn-full-width">Verify</button>
        </form>`,
    );

    const input = document.getElementById('code');
    input.addEventListener('input', () => {
        input.value = input.value.replace(/\D/g, '').slice(0, 6);
    });

    enhanceCodeInput(document.getElementById('code'));

    document.getElementById('mfa-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        setError(null);

        const btn = document.getElementById('submit-btn');
        busy(btn, true);
        try {
            const result = await auth.completeChallenge({
                email: challenge.email,
                session: challenge.session,
                challengeName: 'SOFTWARE_TOKEN_MFA',
                code: input.value,
            });
            if (result.status === 'challenge') {
                renderChallenge(result);
                return;
            }
            showToast('Signed in', 'success');
            await router.navigate(takeReturnTo() ?? '/');
        } catch (err) {
            // The Cognito session is single-use: a wrong code invalidates it and the
            // user has to start again, so say that rather than letting them retype
            // into a form that can no longer succeed.
            setError('That code was not accepted. Please sign in again.');
            setTimeout(() => renderLogin(), 2500);
        }
    });
}
